import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Layers, Building, FileSpreadsheet, Percent, User2, Tag, Download, Upload, Trash2, Link2 } from "lucide-react";
import { canEditMasters } from "@/lib/roles";
import { API } from "@/lib/api";
import { PayeeDuplicatesReport } from "@/components/PayeeDuplicatesReport";
import { BankMissingTab } from "@/components/BankMissingTab";
import { BankRequestsTab } from "@/components/BankRequestsTab";
import { ExpenseLinesCheck } from "@/components/ExpenseLinesCheck";
import { RkfBankAccountsTab } from "@/components/RkfBankAccountsTab";

function BulkUploadCard({ canEdit, onReload }) {
  const [uploading, setUploading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [dryRun, setDryRun] = React.useState(true);
  const [includeExisting, setIncludeExisting] = React.useState(false);
  const [recentBatches, setRecentBatches] = React.useState([]);
  const [tick, setTick] = React.useState(0);
  const [lastFile, setLastFile] = React.useState(null);
  const fileRef = React.useRef(null);

  const loadBatches = async () => {
    try {
      const r = await api.get("/masters/import-batches");
      setRecentBatches(r.data || []);
    } catch { /* ignore */ }
  };
  React.useEffect(() => { if (canEdit) loadBatches(); }, [canEdit]);
  // Re-render every 15s so the "undo window" countdown updates
  React.useEffect(() => { const id = setInterval(() => setTick(t => t + 1), 15000); return () => clearInterval(id); }, []);

  const downloadTemplate = async () => {
    try {
      const r = await api.get(`/masters/bulk-template${includeExisting ? "?include_existing=true" : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = includeExisting ? "my-rkf-masters-populated.xlsx" : "my-rkf-masters-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(includeExisting ? "Populated file downloaded" : "Blank template downloaded");
    } catch (err) {
      toast.error(err.response?.status === 403 ? "Only Admin/Accounts can download the template" : "Template download failed");
    }
  };

  const onPick = () => fileRef.current?.click();

  const runUpload = async (file, isDryRun) => {
    setUploading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/masters/bulk-upload?dry_run=${isDryRun ? "true" : "false"}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      const t = r.data.totals;
      const verb = r.data.dry_run ? "Dry-run" : "Import";
      toast.success(`${verb} · ${t.inserted} ${r.data.dry_run ? "would-add" : "added"} · ${t.skipped} skipped · ${t.errors} errors`);
      if (r.data.dry_run) {
        setLastFile(file);
      } else {
        setLastFile(null);
        onReload && onReload();
        loadBatches();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk upload failed");
    } finally { setUploading(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    await runUpload(file, dryRun);
  };

  const confirmImport = async () => {
    if (!lastFile) return;
    if (!window.confirm(`Import into DB now? ${result?.totals?.inserted || 0} rows will be added.`)) return;
    await runUpload(lastFile, false);
  };

  const undoBatch = async (b) => {
    if (!window.confirm(`Undo this import? Will delete all ${b.totals?.inserted || 0} rows it inserted.`)) return;
    try {
      const r = await api.post(`/masters/import-batches/${b.id}/undo`);
      toast.success(`Undone — ${Object.values(r.data.deleted || {}).reduce((a,c)=>a+c,0)} rows removed`);
      loadBatches();
      onReload && onReload();
    } catch (err) { toast.error(err.response?.data?.detail || "Undo failed"); }
  };

  const ageMinutes = (iso) => {
    try { return Math.floor((Date.now() - new Date(iso).getTime()) / 60000); } catch { return 999; }
  };

  if (!canEdit) return null;

  return (
    <Card className="mb-6 border-slate-300 bg-slate-50/70" data-testid="masters-bulk-card">
      <CardContent className="p-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0">
            <FileSpreadsheet className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-base">Bulk upload masters</div>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">
              Download the .xlsx template, fill one sheet per master (Cost Centres, Budget Heads, Sub-Types,
              Particulars, TDS Sections, Payees), and re-upload. References resolve by name, duplicates skip
              cleanly, so you can re-run the file after fixing errors.
            </p>
            <div className="flex flex-wrap gap-4 mt-2 text-xs text-slate-600">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} data-testid="masters-dry-run-toggle" />
                <b>Dry-run first</b> (validate only, don't write)
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={includeExisting} onChange={(e) => setIncludeExisting(e.target.checked)} data-testid="masters-include-existing-toggle" />
                Pre-fill template with existing rows
              </label>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" onClick={downloadTemplate} data-testid="download-masters-template-btn">
              <Download className="w-4 h-4 mr-1" /> Template
            </Button>
            <Button
              onClick={onPick}
              disabled={uploading}
              className="bg-[#0F172A]"
              data-testid="upload-masters-bulk-btn"
            >
              <Upload className="w-4 h-4 mr-1" />
              {uploading ? "Uploading…" : dryRun ? "Validate File" : "Upload File"}
            </Button>
            <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={onFile} data-testid="upload-masters-input" />
          </div>
        </div>

        {result && (
          <div className="mt-4 border-t pt-4" data-testid="masters-bulk-result">
            <div className="text-sm mb-2 flex items-center justify-between flex-wrap gap-3">
              <div>
                {result.dry_run && <span className="mr-2 inline-block px-2 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800 font-semibold">DRY-RUN — NO CHANGES SAVED</span>}
                <b>{result.totals.inserted}</b> {result.dry_run ? "would-add" : "added"} · <b>{result.totals.skipped}</b> skipped · <b className={result.totals.errors ? "text-red-600" : ""}>{result.totals.errors}</b> errors
              </div>
              {result.dry_run && result.totals.inserted > 0 && result.totals.errors === 0 && lastFile && (
                <Button
                  size="sm"
                  onClick={confirmImport}
                  disabled={uploading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="confirm-bulk-import-btn"
                >
                  <Upload className="w-3.5 h-3.5 mr-1" />
                  {uploading ? "Importing…" : `Confirm & Import ${result.totals.inserted} rows`}
                </Button>
              )}
              {result.dry_run && result.totals.errors > 0 && (
                <span className="text-[11px] text-red-600 font-medium">Fix the errors below and re-upload.</span>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              {Object.entries(result.by_sheet).map(([sheet, s]) => (
                <div key={sheet} className="p-2 rounded border bg-white">
                  <div className="font-medium">{sheet}</div>
                  <div className="text-slate-500">+{s.inserted} · ~{s.skipped} · !{s.errors?.length ?? 0}</div>
                  {s.note && <div className="text-amber-600 text-[10px] mt-1">{s.note}</div>}
                  {s.errors && s.errors.length > 0 && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-red-600 text-[10px]">Show errors</summary>
                      <ul className="text-[10px] text-red-700 mt-1 space-y-0.5">
                        {s.errors.slice(0, 10).map((er, i) => (
                          <li key={i}>Row {er.row}: {er.reason}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {recentBatches.length > 0 && (
          <div className="mt-4 border-t pt-3" data-testid="masters-recent-batches">
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Recent imports</div>
            <ul className="space-y-1.5 text-xs">
              {recentBatches.slice(0, 5).map((b) => {
                const age = ageMinutes(b.ran_at);
                const canUndo = !b.undone && age < 5;
                void tick; // re-render every 15s for age recalc
                return (
                  <li key={b.id} className="flex items-center justify-between gap-3 p-2 rounded bg-white border">
                    <div className="min-w-0 flex-1">
                      <div className="truncate">
                        <span className="font-medium">{b.totals?.inserted || 0} rows</span> by <span className="text-slate-600">{b.actor_name || b.actor_email}</span>
                      </div>
                      <div className="text-[10px] text-slate-500">
                        {age === 0 ? "just now" : `${age} min ago`}
                        {b.undone && <span className="ml-2 text-slate-400">· undone</span>}
                        {!b.undone && !canUndo && <span className="ml-2 text-slate-400">· undo window expired</span>}
                      </div>
                    </div>
                    {canUndo && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px] border-red-300 text-red-700 hover:bg-red-50"
                              onClick={() => undoBatch(b)} data-testid={`undo-batch-${b.id}`}>
                        Undo ({5 - age}m left)
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function useMaster(endpoint, params = "") {
  const [items, setItems] = useState([]);
  const load = async () => setItems((await api.get(`${endpoint}${params}`)).data);
  useEffect(() => { load(); }, [endpoint, params]);
  return { items, reload: load };
}

export default function Masters() {
  const { user } = useAuth();
  const canEdit = canEditMasters(user?.role);
  const isAccountsOrAdmin = user?.role === "Admin" || user?.role === "Accounts";

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Administration</div>
        <h1 className="font-display text-3xl font-extrabold mt-1">Masters</h1>
        <p className="text-sm text-slate-500 mt-1">Heads, Sub-Types, Particulars, Payees, TDS Sections, Cost Centres. {!canEdit && <span className="text-amber-600">Read-only for your role.</span>}</p>
      </div>

      <BulkUploadCard canEdit={isAccountsOrAdmin} onReload={() => window.location.reload()} />

      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "payees"}>
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="payees" data-testid="mtab-payees"><User2 className="w-3.5 h-3.5 mr-1" /> Payees</TabsTrigger>
          <TabsTrigger value="tds" data-testid="mtab-tds"><Percent className="w-3.5 h-3.5 mr-1" /> TDS Sections</TabsTrigger>
          <TabsTrigger value="cc" data-testid="mtab-cc"><Building className="w-3.5 h-3.5 mr-1" /> Cost Centres</TabsTrigger>
          <TabsTrigger value="catheads" data-testid="mtab-catheads"><Layers className="w-3.5 h-3.5 mr-1" /> Categories &amp; Standard Heads</TabsTrigger>
          {isAccountsOrAdmin && <TabsTrigger value="bankverify" data-testid="mtab-bank-verify"><User2 className="w-3.5 h-3.5 mr-1" /> Bank details to verify</TabsTrigger>}
          {isAccountsOrAdmin && <TabsTrigger value="bank" data-testid="mtab-bank-missing"><User2 className="w-3.5 h-3.5 mr-1" /> Missing bank details</TabsTrigger>}
          {user?.role === "Admin" && <TabsTrigger value="elc" data-testid="mtab-expense-lines-check"><Layers className="w-3.5 h-3.5 mr-1" /> Expense lines check</TabsTrigger>}
          {user?.role === "Admin" && <TabsTrigger value="dups" data-testid="mtab-dups"><User2 className="w-3.5 h-3.5 mr-1" /> Duplicate payees</TabsTrigger>}
          {isAccountsOrAdmin && <TabsTrigger value="rkfbank" data-testid="mtab-rkf-banks"><Building className="w-3.5 h-3.5 mr-1" /> RKF bank accounts</TabsTrigger>}
        </TabsList>
        <TabsContent value="payees"><PayeesTab canEdit={isAccountsOrAdmin} /></TabsContent>
        <TabsContent value="tds"><TDSTab canEdit={canEdit} /></TabsContent>
        <TabsContent value="cc"><CostCentreTab canEdit={canEdit} /></TabsContent>
        <TabsContent value="catheads"><CategoriesHeadsTab /></TabsContent>
        {isAccountsOrAdmin && <TabsContent value="bankverify"><BankRequestsTab /></TabsContent>}
        {isAccountsOrAdmin && <TabsContent value="bank"><BankMissingTab /></TabsContent>}
        {user?.role === "Admin" && <TabsContent value="elc"><ExpenseLinesCheck /></TabsContent>}
        {user?.role === "Admin" && <TabsContent value="dups"><PayeeDuplicatesReport /></TabsContent>}
        {isAccountsOrAdmin && <TabsContent value="rkfbank"><RkfBankAccountsTab canEdit={user?.role === "Admin"} /></TabsContent>}
      </Tabs>
    </div>
  );
}

function SimpleAdd({ label, testId, onAdd, canEdit, children, submit }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const inflight = React.useRef(false);
  if (!canEdit) return null;
  const onSubmit = async (e) => {
    e.preventDefault();
    if (inflight.current) return;
    inflight.current = true;
    setBusy(true);
    try { await submit(); setOpen(false); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } finally { inflight.current = false; setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="bg-[#0F172A]" data-testid={testId}><Plus className="w-4 h-4 mr-1" /> {label}</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{label}</DialogTitle></DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          {children}
          <DialogFooter><Button type="submit" disabled={busy} data-testid={`${testId}-save`}>{busy ? "Saving…" : "Save"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function HeadsTab({ canEdit }) {
  const { items, reload } = useMaster("/masters/heads");
  const [f, setF] = useState({ name: "", code: "", description: "" });
  const submit = async () => { await api.post("/masters/heads", f); toast.success("Added"); setF({ name: "", code: "", description: "" }); reload(); };
  const del = async (id, label) => {
    if (!window.confirm(`Delete "${label}"? Refused if any sub-type or budget still uses it.`)) return;
    try { await api.delete(`/masters/heads/${id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div>
      <div className="flex justify-end mb-3">
        <SimpleAdd label="Budget Head" testId="add-head-btn" canEdit={canEdit} submit={submit}>
          <div><Label>Name</Label><Input required value={f.name} onChange={(e) => setF({...f, name: e.target.value})} placeholder="e.g. Personnel, Travel, Training" data-testid="head-name-master-input" /></div>
          <div><Label>Code</Label><Input value={f.code} onChange={(e) => setF({...f, code: e.target.value})} /></div>
          <div><Label>Description</Label><Textarea value={f.description} onChange={(e) => setF({...f, description: e.target.value})} /></div>
        </SimpleAdd>
      </div>
      <MasterTable rows={items.map(x => ({ id: x.id, Name: x.name, Code: x.code || "—", Description: x.description || "—" }))}
        testIdPrefix="head" onDelete={canEdit ? (r) => del(r.id, r.Name) : null} />
    </div>
  );
}

function SubTypesTab({ canEdit }) {
  const { items, reload } = useMaster("/masters/sub-types");
  const { items: heads } = useMaster("/masters/heads");
  const { items: projects } = useMaster("/projects");
  const { items: costCentres } = useMaster("/cost-centres");
  const [f, setF] = useState({ head_id: "", name: "", project_id: "", cost_centre_id: "" });
  const submit = async () => {
    const payload = { head_id: f.head_id, name: f.name };
    if (f.project_id) payload.project_id = f.project_id;
    if (f.cost_centre_id) payload.cost_centre_id = f.cost_centre_id;
    await api.post("/masters/sub-types", payload);
    toast.success("Added"); setF({ head_id: "", name: "", project_id: "", cost_centre_id: "" }); reload();
  };
  const del = async (id, label) => {
    if (!window.confirm(`Delete sub-type "${label}"? Refused if any particular or budget line still uses it.`)) return;
    try { await api.delete(`/masters/sub-types/${id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const headMap = Object.fromEntries(heads.map(h => [h.id, h.name]));
  const projMap = Object.fromEntries(projects.map(p => [p.id, p.name]));
  const ccMap = Object.fromEntries(costCentres.map(c => [c.id, c.name]));
  return (
    <div>
      <div className="flex justify-end mb-3">
        <SimpleAdd label="Sub-Type" testId="add-subtype-btn" canEdit={canEdit} submit={submit}>
          <div>
            <Label>Head</Label>
            <Select value={f.head_id} onValueChange={(v) => setF({...f, head_id: v})}>
              <SelectTrigger data-testid="subtype-head-select"><SelectValue placeholder="Select head" /></SelectTrigger>
              <SelectContent>{heads.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Name</Label><Input required value={f.name} onChange={(e) => setF({...f, name: e.target.value})} placeholder="e.g. Paramedical Staff" data-testid="subtype-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Project</Label>
              <Select value={f.project_id} onValueChange={(v) => setF({...f, project_id: v, cost_centre_id: ""})}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Cost Centre</Label>
              <Select value={f.cost_centre_id} onValueChange={(v) => setF({...f, cost_centre_id: v, project_id: ""})}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{costCentres.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </SimpleAdd>
      </div>
      <MasterTable rows={items.map(x => ({
        id: x.id, Name: x.name, Head: headMap[x.head_id] || "—",
        "Belongs to": x.project_id ? `Project · ${projMap[x.project_id] || "—"}` : `Cost Centre · ${ccMap[x.cost_centre_id] || "—"}`,
        Status: x.status,
      }))} testIdPrefix="subtype" onDelete={canEdit ? (r) => del(r.id, r.Name) : null} />
    </div>
  );
}

function ParticularsTab({ canEdit }) {
  const { items, reload } = useMaster("/masters/particulars");
  const { items: subtypes } = useMaster("/masters/sub-types");
  const [f, setF] = useState({ sub_type_id: "", name: "" });
  const submit = async () => { await api.post("/masters/particulars", f); toast.success("Added"); setF({ sub_type_id: "", name: "" }); reload(); };
  const del = async (id, label) => {
    if (!window.confirm(`Delete particular "${label}"?`)) return;
    try { await api.delete(`/masters/particulars/${id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const stMap = Object.fromEntries(subtypes.map(s => [s.id, s.name]));
  return (
    <div>
      <div className="flex justify-end mb-3">
        <SimpleAdd label="Particular" testId="add-particular-btn" canEdit={canEdit} submit={submit}>
          <div>
            <Label>Sub-Type</Label>
            <Select value={f.sub_type_id} onValueChange={(v) => setF({...f, sub_type_id: v})}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{subtypes.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Particular</Label><Input required value={f.name} onChange={(e) => setF({...f, name: e.target.value})} data-testid="particular-name-input" /></div>
        </SimpleAdd>
      </div>
      <MasterTable rows={items.map(x => ({ id: x.id, Name: x.name, "Sub-Type": stMap[x.sub_type_id] || "—" }))}
        testIdPrefix="particular" onDelete={canEdit ? (r) => del(r.id, r.Name) : null} />
    </div>
  );
}

function PayeesTab({ canEdit }) {
  const { items, reload } = useMaster("/masters/payees");
  const { items: tds } = useMaster("/masters/tds-sections");
  const [f, setF] = useState({ name: "", contact: "", payee_type: "Vendor", pan: "", gstin: "", bank: "", account_number: "", ifsc: "", tds_section_id: "", default_tds_rate: 0 });
  const submit = async () => { await api.post("/masters/payees", { ...f, default_tds_rate: parseFloat(f.default_tds_rate || 0) }); toast.success("Added"); reload(); };
  const del = async (id, label) => {
    if (!window.confirm(`Delete payee "${label}"? Refused if any reimbursement or advance still uses it.`)) return;
    try { await api.delete(`/masters/payees/${id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const [onlyPending, setOnlyPending] = useState(false);
  const pendingCount = items.filter(x => x.status === "pending").length;
  const verify = async (x) => {
    if (!window.confirm(`Verify "${x.name}"? Check PAN, GSTIN and bank details first.`)) return;
    try { await api.post(`/v2/payees/${x.id}/verify`); toast.success(`${x.name} verified`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Verify failed"); }
  };
  const statusCell = (x, i) => x.status === "pending"
    ? <span className="flex items-center gap-2"><Badge variant="outline" className="text-amber-700 border-amber-300">pending</Badge>{canEdit && <Button size="sm" variant="outline" className="h-7" onClick={() => verify(x)} data-testid={`payee-verify-${i}`}>Verify</Button>}</span>
    : <Badge variant="outline" className="capitalize">{x.status || "verified"}</Badge>;
  const shown = onlyPending ? items.filter(x => x.status === "pending") : items;
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <Button size="sm" variant={onlyPending ? "default" : "outline"} onClick={() => setOnlyPending(!onlyPending)} data-testid="payee-pending-filter">
          {onlyPending ? "Show all" : `Pending verification (${pendingCount})`}
        </Button>
        <SimpleAdd label="Payee" testId="add-payee-btn" canEdit={canEdit} submit={submit}>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input required value={f.name} onChange={(e) => setF({...f, name: e.target.value})} data-testid="payee-name-input" /></div>
            <div><Label>Type</Label>
              <Select value={f.payee_type} onValueChange={(v) => setF({...f, payee_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Vendor">Vendor</SelectItem><SelectItem value="Employee">Employee</SelectItem><SelectItem value="Contractor">Contractor</SelectItem><SelectItem value="Landlord">Landlord</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Contact</Label><Input value={f.contact} onChange={(e) => setF({...f, contact: e.target.value})} /></div>
            <div><Label>PAN</Label><Input value={f.pan} onChange={(e) => setF({...f, pan: e.target.value})} /></div>
            <div><Label>GSTIN</Label><Input value={f.gstin} onChange={(e) => setF({...f, gstin: e.target.value})} /></div>
            <div><Label>Bank</Label><Input value={f.bank} onChange={(e) => setF({...f, bank: e.target.value})} /></div>
            <div><Label>Account Number</Label><Input value={f.account_number} onChange={(e) => setF({...f, account_number: e.target.value})} data-testid="payee-account-input" /></div>
            <div><Label>IFSC</Label><Input value={f.ifsc} onChange={(e) => setF({...f, ifsc: e.target.value})} /></div>
            <div>
              <Label>TDS Section</Label>
              <Select value={f.tds_section_id} onValueChange={(v) => setF({...f, tds_section_id: v})}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{tds.map(t => <SelectItem key={t.id} value={t.id}>{t.section} · {t.default_rate}%</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Default TDS %</Label><Input type="number" value={f.default_tds_rate} onChange={(e) => setF({...f, default_tds_rate: e.target.value})} /></div>
          </div>
        </SimpleAdd>
      </div>
      <MasterTable rows={shown.map((x, i) => ({ id: x.id, Name: x.name, Type: x.payee_type || x.payee_role || "—", Status: statusCell(x, i), PAN: x.pan || "—", Bank: x.bank || "—", "Acct.": x.account_number || "—", IFSC: x.ifsc || "—", "TDS %": x.default_tds_rate }))}
        testIdPrefix="payee" onDelete={canEdit ? (r) => del(r.id, r.Name) : null} />
    </div>
  );
}

function TDSTab({ canEdit }) {
  const { items, reload } = useMaster("/masters/tds-sections");
  const [f, setF] = useState({ section: "", description: "", default_rate: 0 });
  const submit = async () => { await api.post("/masters/tds-sections", { ...f, default_rate: parseFloat(f.default_rate) }); toast.success("Added"); setF({ section: "", description: "", default_rate: 0 }); reload(); };
  const del = async (id, label) => {
    if (!window.confirm(`Delete TDS section "${label}"? Refused if any payee/reimbursement/advance still uses it.`)) return;
    try { await api.delete(`/masters/tds-sections/${id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div>
      <div className="flex justify-end mb-3">
        <SimpleAdd label="TDS Section" testId="add-tds-btn" canEdit={canEdit} submit={submit}>
          <div><Label>Section</Label><Input required value={f.section} onChange={(e) => setF({...f, section: e.target.value})} placeholder="e.g. 194C, 194J" data-testid="tds-section-input" /></div>
          <div><Label>Description</Label><Input value={f.description} onChange={(e) => setF({...f, description: e.target.value})} /></div>
          <div><Label>Default Rate %</Label><Input required type="number" step="0.01" value={f.default_rate} onChange={(e) => setF({...f, default_rate: e.target.value})} data-testid="tds-rate-input" /></div>
        </SimpleAdd>
      </div>
      <MasterTable rows={items.map(x => ({ id: x.id, Section: x.section, Description: x.description || "—", Rate: `${x.default_rate}%` }))}
        testIdPrefix="tds" onDelete={canEdit ? (r) => del(r.id, r.Section) : null} />
    </div>
  );
}

function CostCentreTab({ canEdit }) {
  const { items, reload } = useMaster("/cost-centres");
  const [f, setF] = useState({ name: "", code: "", description: "" });
  const submit = async () => { await api.post("/cost-centres", f); toast.success("Added"); setF({ name: "", code: "", description: "" }); reload(); };
  const del = async (id, label) => {
    if (!window.confirm(`Delete cost centre "${label}"?`)) return;
    try { await api.delete(`/cost-centres/${id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div>
      <div className="flex justify-end mb-3">
        <SimpleAdd label="Cost Centre" testId="add-cc-btn" canEdit={canEdit} submit={submit}>
          <div><Label>Name</Label><Input required value={f.name} onChange={(e) => setF({...f, name: e.target.value})} placeholder="e.g. Corporate Office" data-testid="cc-name-input" /></div>
          <div><Label>Code</Label><Input value={f.code} onChange={(e) => setF({...f, code: e.target.value})} /></div>
          <div><Label>Description</Label><Textarea value={f.description} onChange={(e) => setF({...f, description: e.target.value})} /></div>
        </SimpleAdd>
      </div>
      <MasterTable rows={items.map(x => ({ id: x.id, Name: x.name, Code: x.code || "—", Description: x.description || "—" }))}
        testIdPrefix="cc" onDelete={canEdit ? (r) => del(r.id, r.Name) : null} />
    </div>
  );
}

function MasterTable({ rows, testIdPrefix, onDelete }) {
  if (!rows.length) return <Card className="border-dashed"><CardContent className="p-10 text-center text-sm text-slate-500">No records yet.</CardContent></Card>;
  const cols = Object.keys(rows[0]).filter(c => c !== "id");
  return (
    <Card>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500">
            <tr className="border-b">
              {cols.map(c => <th key={c} className="text-left px-4 py-2.5">{c}</th>)}
              {onDelete && <th className="w-10 px-2 py-2.5"></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id || i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`${testIdPrefix}-row-${i}`}>
                {cols.map(c => <td key={c} className="px-4 py-2.5">{r[c]}</td>)}
                {onDelete && (
                  <td className="px-2 py-2.5 text-right">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => onDelete(r)} data-testid={`${testIdPrefix}-delete-${i}`} title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function CategoriesHeadsTab() {
  const [cats, setCats] = React.useState([]);
  const [heads, setHeads] = React.useState([]);
  React.useEffect(() => {
    api.get("/budget-categories").then(r => setCats(r.data)).catch(() => {});
    api.get("/standard-heads").then(r => setHeads(r.data)).catch(() => {});
  }, []);
  return (
    <div className="space-y-6" data-testid="masters-catheads">
      <Card className="border-slate-200 bg-slate-50/50">
        <CardContent className="p-4 text-sm text-slate-600">
          <b>View-only.</b> Categories and Standard Heads are system-seeded and cannot be edited from Masters — they are the fixed spine of Budget V2. Project-specific heads and sub-types are managed under <b>Budgets</b>.
        </CardContent>
      </Card>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Categories ({cats.length})</div>
        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="cat-table">
            <thead className="bg-slate-100 text-xs uppercase text-slate-600"><tr><th className="p-2 text-left">Code</th><th className="p-2 text-left">Name</th><th className="p-2 text-left">Description</th></tr></thead>
            <tbody>
              {cats.map(c => (
                <tr key={c.code} className="border-t" data-testid={`cat-row-${c.code}`}><td className="p-2 font-mono">{c.code}</td><td className="p-2 font-medium">{c.name}</td><td className="p-2 text-slate-500">{c.description || "—"}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Standard Heads ({heads.length})</div>
        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="stdhead-table">
            <thead className="bg-slate-100 text-xs uppercase text-slate-600"><tr><th className="p-2 text-left">Code</th><th className="p-2 text-left">Name</th><th className="p-2 text-left">Category</th></tr></thead>
            <tbody>
              {heads.map(h => (
                <tr key={h.code} className="border-t" data-testid={`stdhead-row-${h.code}`}><td className="p-2 font-mono">{h.code}</td><td className="p-2 font-medium">{h.name}</td><td className="p-2 text-slate-500 font-mono">{h.category_code || "—"}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AliasesTab({ canEdit }) {
  const [aliases, setAliases] = React.useState([]);
  const [filter, setFilter] = React.useState("all");
  const [loading, setLoading] = React.useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/masters/aliases");
      setAliases(r.data || []);
    } catch { setAliases([]); }
    setLoading(false);
  };
  React.useEffect(() => { load(); }, []);

  const remove = async (a) => {
    if (!window.confirm(`Remove alias "${a.alias_display}" → ${a.master_name}?`)) return;
    try {
      await api.delete(`/masters/aliases/${a.id}`);
      toast.success("Alias removed");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const filtered = filter === "all" ? aliases : aliases.filter(a => a.master_type === filter);

  return (
    <div>
      <Card className="mb-4 bg-slate-50 border-slate-200">
        <CardContent className="p-4">
          <div className="text-sm text-slate-700">
            <b>Aliases</b> remember uploaded text that used to fail exact-match against Masters. Once saved, the same text on a future budget upload auto-maps to this Master with zero user action. Add aliases from the Review Mapping screen at upload/reconcile time.
          </div>
        </CardContent>
      </Card>
      <div className="flex gap-2 mb-3">
        <Button size="sm" variant={filter === "all" ? "default" : "outline"} onClick={() => setFilter("all")} data-testid="alias-filter-all">All ({aliases.length})</Button>
        <Button size="sm" variant={filter === "head" ? "default" : "outline"} onClick={() => setFilter("head")} data-testid="alias-filter-head">Heads ({aliases.filter(a => a.master_type === "head").length})</Button>
        <Button size="sm" variant={filter === "subtype" ? "default" : "outline"} onClick={() => setFilter("subtype")} data-testid="alias-filter-subtype">Sub-Types ({aliases.filter(a => a.master_type === "subtype").length})</Button>
      </div>
      {loading ? <div className="text-sm text-slate-500 p-4">Loading…</div> :
        filtered.length === 0 ? <Card className="border-dashed"><CardContent className="p-10 text-center text-sm text-slate-500">No aliases yet. They're created when you tick "Remember" during a budget review.</CardContent></Card> :
        <Card><CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500">
              <tr className="border-b">
                <th className="text-left px-4 py-2.5">Type</th>
                <th className="text-left px-4 py-2.5">Uploaded Text</th>
                <th className="text-left px-4 py-2.5">Normalised</th>
                <th className="text-left px-4 py-2.5">Maps To Master</th>
                <th className="text-left px-4 py-2.5">Created</th>
                {canEdit && <th className="w-10 px-2 py-2.5"></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((a, i) => (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`alias-row-${i}`}>
                  <td className="px-4 py-2.5"><Badge variant="outline" className="text-[10px]">{a.master_type}</Badge></td>
                  <td className="px-4 py-2.5 font-mono text-xs">{a.alias_display}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{a.alias_normalised}</td>
                  <td className="px-4 py-2.5 font-medium">{a.master_name || <span className="text-red-600">(Master deleted)</span>}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{a.created_at?.slice(0, 10)}</td>
                  {canEdit && <td className="px-2 py-2.5 text-right">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => remove(a)} data-testid={`alias-delete-${i}`} title="Delete alias">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent></Card>
      }
    </div>
  );
}

